/**
 * SOLID Review
 * S — Good. One concern: the client's two stores as the two state ports — the contracted read and write
 *     the Application uses, and the by-reference view React renders from.
 * O — Good. A new field needs only the projections; a new store would be one more host resource.
 * L — Strong. Implements both port interfaces exactly.
 * I — Good. The four members the two ports declare.
 * D — Strong. The stores arrive as Application-declared host interfaces; the projections are the Application's.
 */
import type { IContextRequest } from '../../domain/ports/schemas/ContextRequest';
import type { IPropsClientChanges, IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { IPortClientState } from '../../application/ports/contracts';
import type { IPortClientStateView, TClientViewState, IEntitySessionView } from '../../application/ports/ClientStateView';
import type { GameStore, IServiceTableStore } from '../../application/state/gameStoreTypes';
import type { IServiceSessionStore, IStateSessionHost } from '../../application/state/sessionStoreTypes';
import { projectClientTable } from '../../application/state/clientTableProjection';
import { projectClientSession, sessionWrites, sessionViewOf } from '../../application/state/clientSessionProjection';
import { sameJson } from '../../shared/sameJson';

/**
 * THE STORES BEHIND THE PORTS (frontend strict-ports plan, FE1; the session since FE2, 2026-10-03). The
 * two zustand stores sit beside this class since FE6c (`gameStore.ts`, `authStore.ts`, both data), handed
 * in by the composition root, and this is the one seam every decision's changes go through.
 *
 * ONE WRITE PER STORE PER DECISION. Changes are applied in the order the changes schema states —
 * `reset` first (the game store's `resetAll`), then every table field, the activity stamp and the
 * session's game-store fields together in ONE `setState`, then the session's auth-store fields in one
 * more — so React never renders a half-applied decision. `markActivity` becomes
 * `lastGameActivityAt: now`. `persist` is not this port's: the Application keeps it through
 * `ClientTabStorage` in the same step.
 *
 * THE VIEW is the game store's own state with the session beside it. It is rebuilt only when either
 * store's state object changes, so `getSnapshot` is stable between writes, as `useSyncExternalStore`
 * requires; the session view is rebuilt only when the auth store's changes, so a component selecting
 * `session.account` re-renders for an account change and nothing else. Both view members are bound,
 * because React calls them detached.
 */
export class ClientStateStore implements IPortClientState, IPortClientStateView {
  private viewOf: { game: GameStore; auth: IStateSessionHost; view: TClientViewState } | null = null;
  private sessionViewCache: {
    auth: IStateSessionHost; localUser: GameStore['localUser']; roomId: string | null; session: IEntitySessionView;
  } | null = null;

  constructor(
    private readonly tables: IServiceTableStore,
    private readonly sessions: IServiceSessionStore,
    private readonly now: () => number = Date.now,
  ) {}

  async snapshot(_ctx: IContextRequest) {
    const game = this.tables.getState();
    const input: IPropsClientInput = { table: projectClientTable(game), session: projectClientSession(game, this.sessions.getState()) };
    return { success: true as const, value: input };
  }

  async apply(_ctx: IContextRequest, changes: IPropsClientChanges) {
    const { reset, markActivity, session, persist: _persist, ...fields } = changes;
    if (reset === true) this.tables.getState().resetAll();
    const writes = session === undefined ? null : sessionWrites(session);
    const patch: Partial<GameStore> = { ...fields, ...writes?.game };
    // Derived values equal to the ones held keep the HELD object: readers of `derived.*` select by
    // reference, and an identical new object would re-render every one of them.
    if (patch.derived !== undefined && sameJson(patch.derived, this.tables.getState().derived)) delete patch.derived;
    if (markActivity === true) patch.lastGameActivityAt = this.now();
    if (Object.keys(patch).length > 0) this.tables.setState(patch);
    if (writes !== null && Object.keys(writes.auth).length > 0) this.sessions.setState(writes.auth);
    return { success: true as const, value: undefined };
  }

  // Arrow fields, not methods: React hands these to `useSyncExternalStore` unbound.
  readonly getSnapshot = (): TClientViewState => {
    const game = this.tables.getState();
    const auth = this.sessions.getState();
    if (this.viewOf !== null && this.viewOf.game === game && this.viewOf.auth === auth) return this.viewOf.view;
    const view: TClientViewState = { ...game, session: this.sessionView(auth) };
    this.viewOf = { game, auth, view };
    return view;
  };

  readonly subscribe = (listener: () => void): (() => void) => {
    const stopTables = this.tables.subscribe(listener);
    const stopSessions = this.sessions.subscribe(listener);
    return () => {
      stopTables();
      stopSessions();
    };
  };

  /**
   * Rebuilt when the auth store, the seated player or the table's room changes — the ad zone follows the
   * player's credits, the video chat the room.
   */
  private sessionView(auth: IStateSessionHost): IEntitySessionView {
    const game = this.tables.getState();
    const roomId = game.room?.roomId ?? null;
    const cached = this.sessionViewCache;
    if (cached !== null && cached.auth === auth && cached.localUser === game.localUser && cached.roomId === roomId) return cached.session;
    const session = sessionViewOf(game, auth);
    this.sessionViewCache = { auth, localUser: game.localUser, roomId, session };
    return session;
  }

}
